import React, { useState, useRef, useEffect } from "react";
import styles from "./ReactionCapabilitiesTestingEquipments.module.scss";
import Image from "next/image";

// Dummy data for reaction capabilities
const reactionCapabilitiesData = [
  {
    id: 1,
    name: "Acetylation",
    description:
      "Introduces acetyl groups to enhance solubility, stability, and performance in final formulations.",
  },
  {
    id: 2,
    name: "Alkali Metal Reactions",
    description:
      "Utilizes reactive alkali metals for unique chemical transformations under controlled environments.",
  },
  {
    id: 3,
    name: "Alkylation",
    description:
      "Adds alkyl chains to increase hydrophobicity, improving product efficacy and stability.",
  },
  {
    id: 4,
    name: "Amidation",
    description:
      "Forms stable amide bonds essential for pharmaceuticals and specialty intermediates.",
  },
  {
    id: 5,
    name: "Bromination",
    description:
      "Introduces bromine atoms for enhanced flame retardancy, reactivity, or selectivity.",
  },
  {
    id: 6,
    name: "Bromine Recovery",
    description:
      "Captures and recycles bromine to reduce waste and improve sustainability.",
  },
  {
    id: 7,
    name: "Catalytic Hydrogenation",
    description:
      "Reduces compounds efficiently using catalysts for high selectivity and purity.",
  },
  {
    id: 8,
    name: "Chlorination",
    description:
      "Incorporates chlorine atoms to boost chemical reactivity and functional versatility.",
  },
  {
    id: 9,
    name: "Cross-Coupling Reactions",
    description:
      "Enables carbon-carbon bond formation for complex molecule construction.",
  },
  {
    id: 10,
    name: "Dehydrogenation",
    description:
      "Removes hydrogen atoms to create double bonds or aromatic compounds efficiently.",
  },
  {
    id: 11,
    name: "Distillation",
    description:
      "Separates and purifies components with precision for consistent product quality.",
  },
  {
    id: 12,
    name: "Esterification",
    description:
      "Forms esters used in flavors, fragrances, and specialty solvents with tailored properties.",
  },
  {
    id: 13,
    name: "Ethoxylation",
    description:
      "Adds ethylene oxide units to improve solubility and emulsification characteristics.",
  },
  {
    id: 14,
    name: "Fluorine Chemistry (HF/KF)",
    description:
      "Applies fluorinating agents for high-performance materials and agrochemical intermediates.",
  },
  {
    id: 15,
    name: "Friedel Crafts Reaction",
    description:
      "Performs selective alkylation or acylation on aromatic compounds using Lewis acids.",
  },
  {
    id: 16,
    name: "Grignard Reaction",
    description:
      "Forms carbon-carbon bonds with organomagnesium reagents for fine chemical synthesis.",
  },
  {
    id: 17,
    name: "Halogenation",
    description:
      "Inserts halogen atoms to increase chemical activity or functional versatility.",
  },
  {
    id: 18,
    name: "High-Vacuum Distillation",
    description:
      "Purifies heat-sensitive or high-boiling compounds under reduced pressure.",
  },
  {
    id: 19,
    name: "Hoffman Reaction",
    description:
      "Produces primary amines from amides using base-promoted rearrangement.",
  },
  {
    id: 20,
    name: "Hydrogenation",
    description:
      "Reduces unsaturated compounds using hydrogen gas for refined product profiles.",
  },
  {
    id: 21,
    name: "Kolbe Schmitt Reaction",
    description:
      "Synthesizes aromatic acids from phenols and CO₂ under pressure.",
  },
  {
    id: 22,
    name: "Nitration",
    description:
      "Introduces nitro groups to aromatic systems for use in dyes and pharmaceuticals.",
  },
  {
    id: 23,
    name: "Oxidation",
    description:
      "Enhances functionality by introducing oxygen atoms for targeted performance.",
  },
  {
    id: 24,
    name: "Oximation",
    description:
      "Forms oximes from carbonyl compounds, enabling further synthesis or separation.",
  },
  {
    id: 25,
    name: "Polymerisation",
    description:
      "Builds polymer chains for applications in coatings, adhesives, and elastomers.",
  },
  {
    id: 26,
    name: "Reduction",
    description:
      "Transforms functional groups to simpler forms for desired reactivity or stability.",
  },
  {
    id: 27,
    name: "Vapour Phase Chlorination",
    description:
      "Performs gas-phase chlorination for consistent, scalable production of chlorinated products.",
  },
];

const testingEquipmentsData = [
  {
    id: 1,
    name: "Liquid Chromatography",
    description:
      "Separates and analyzes mixtures based on interactions with a stationary and mobile phase.",
  },
  {
    id: 2,
    name: "Gas Chromatography",
    description:
      "Identifies and quantifies volatile compounds in a sample with high precision.",
  },
  {
    id: 3,
    name: "Karl-Fischer Titration",
    description: "Measures moisture content accurately in liquids and solids.",
  },
  {
    id: 4,
    name: "UV Spectro Photo Meter",
    description:
      "Analyzes compound absorbance across UV and visible light for concentration estimation.",
  },
  {
    id: 5,
    name: "Muffle Furnace",
    description:
      "Provides high-temperature heating for ash content, combustion, and thermal decomposition studies.",
  },
  {
    id: 6,
    name: "Hot Air Oven",
    description:
      "Used for drying, sterilizing, and stability testing at controlled temperatures.",
  },
  {
    id: 7,
    name: "Millipore Vaccum Pump",
    description:
      "Generates vacuum for filtration and sample preparation in analytical labs.",
  },
  {
    id: 8,
    name: "Mioisture Analyzer",
    description:
      "Determines moisture percentage by heating and weight loss measurement.",
  },
  {
    id: 9,
    name: "Digital Microscope",
    description:
      "Magnifies and captures surface-level features and particle morphology.",
  },
  {
    id: 10,
    name: "COD Apparatus",
    description:
      "Measures chemical oxygen demand to assess water quality and organic content.",
  },
  {
    id: 11,
    name: "Sonicator",
    description:
      "Uses ultrasonic waves to disperse particles or break down materials in liquid samples.",
  },
  {
    id: 12,
    name: "UV Cabinet",
    description:
      "Facilitates compound detection under UV light during chromatographic or staining procedures.",
  },
  {
    id: 13,
    name: "Thermoven with SS Trays",
    description:
      "Provides uniform heating for drying or curing with stainless steel trays.",
  },
  {
    id: 14,
    name: "Melting Range Apparatus",
    description:
      "Determines the temperature range over which a substance melts.",
  },
  {
    id: 15,
    name: "Mechanical Stirrer",
    description:
      "Ensures consistent mixing and agitation of solutions in batch processing.",
  },
  {
    id: 16,
    name: "Automatic Ice Flaker",
    description:
      "Produces flaked ice for sample preservation and cold chain lab operations.",
  },
];

const ReactionCapabilityCard = ({ reaction }) => {
  return (
    <div className={styles.reactionCapabilityCard}>
      {/* Image */}
      {/* <div className={styles.imageContainer}>
        <Image
          src={getCDNImageUrl("dummy-reaction.webp")}
          alt={reaction.name}
          className={styles.image}
          fill
        />
      </div> */}
      {/* Reaction Name */}
      <h3 className={styles.reactionName}>{reaction.name}</h3>
      {/* Reaction Description */}
      <p className={styles.reactionDescription}>{reaction.description}</p>
    </div>
  );
};

const TestingEquipementCard = ({ equipment }) => {
  return (
    <div className={styles.reactionCapabilityCard}>
      {/* Image */}
      {/* <div className={styles.imageContainer}>
        <Image
          src={getCDNImageUrl("Testing-Equipment-Dummy.webp")}
          alt={equipment.name}
          className={styles.image}
          style={{ objectFit: "cover" }}
          fill
        />
      </div> */}
      {/* Equipment Name */}
      <h3 className={styles.reactionName}>{equipment.name}</h3>
      {/* Equipment Description */}
      <p className={styles.reactionDescription}>{equipment.description}</p>
    </div>
  );
};

function ReactionCapabilitiesTestingEquipments() {
  const [selectedTab, setSelectedTab] = useState("Reaction Capabilities");
  const [indicatorStyle, setIndicatorStyle] = useState({});
  const [showAllReactions, setShowAllReactions] = useState(false);
  const [showAllEquipments, setShowAllEquipments] = useState(false);
  const [cardsToShow, setCardsToShow] = useState(8); // default fallback
  const [equipmentsToShow, setEquipmentsToShow] = useState(8); // default fallback
  const tabRefs = useRef({});
  const reactionGridRef = useRef(null);
  const equipmentGridRef = useRef(null);

  const tabs = [
    {
      id: "reaction",
      label: "Reaction Capabilities",
      value: "Reaction Capabilities",
    },
    { id: "testing", label: "Testing Equipments", value: "Testing Equipments" },
  ];

  const updateIndicatorPosition = (tabValue) => {
    const selectedTabData = tabs.find((tab) => tab.value === tabValue);
    if (selectedTabData && tabRefs.current[selectedTabData.id]) {
      const tabElement = tabRefs.current[selectedTabData.id];
      const { offsetLeft, offsetWidth } = tabElement;

      setIndicatorStyle({
        transform: `translateX(${offsetLeft}px)`,
        width: `${offsetWidth}px`,
      });
    }
  };

  // Helper to get columns count from grid
  const getColumnsCount = (gridRef) => {
    if (!gridRef.current) return 1;
    const style = window.getComputedStyle(gridRef.current);
    const columns = style.gridTemplateColumns.split(" ").length;
    return columns;
  };

  // Update cardsToShow for Reaction Capabilities
  useEffect(() => {
    const updateCardsToShow = () => {
      if (reactionGridRef.current) {
        const columns = getColumnsCount(reactionGridRef);
        setCardsToShow(columns * 2);
      }
    };
    updateCardsToShow();
    window.addEventListener("resize", updateCardsToShow);
    return () => window.removeEventListener("resize", updateCardsToShow);
  }, [selectedTab]);

  // Update cardsToShow for Testing Equipments
  useEffect(() => {
    const updateEquipmentsToShow = () => {
      if (equipmentGridRef.current) {
        const columns = getColumnsCount(equipmentGridRef);
        setEquipmentsToShow(columns * 2);
      }
    };
    updateEquipmentsToShow();
    window.addEventListener("resize", updateEquipmentsToShow);
    return () => window.removeEventListener("resize", updateEquipmentsToShow);
  }, [selectedTab]);

  useEffect(() => {
    // Update indicator position when component mounts or selectedTab changes
    updateIndicatorPosition(selectedTab);
  }, [selectedTab]);

  useEffect(() => {
    // Handle window resize
    const handleResize = () => {
      updateIndicatorPosition(selectedTab);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [selectedTab]);

  const handleTabClick = (tabValue) => {
    setSelectedTab(tabValue);
  };

  return (
    <div className={styles.reactionCapabilitiesTestingEquipments}>
      {/* Tabs */}
      <div className={styles.tabsContainer}>
        {tabs.map((tab, index) => (
          <div
            key={tab.id}
            ref={(el) => (tabRefs.current[tab.id] = el)}
            className={`${styles.tab} ${
              selectedTab === tab.value ? styles.active : ""
            }`}
            onClick={() => handleTabClick(tab.value)}
          >
            {tab.label}
          </div>
        ))}
        {/* Moving Indicator */}
        <div className={styles.indicator} style={indicatorStyle} />
      </div>

      {/* Active Selection - Reaction Capabilities / Testing Equipments */}
      {/* Reaction Capabilities Section */}
      {selectedTab === "Reaction Capabilities" && (
        <div className={styles.reactionCapabilities}>
          {/* Heading */}
          <h2 className={styles.heading}>Reaction Capabilities</h2>
          {/* Grid of Reaction Capability cards */}
          <div className={styles.cardsGrid} ref={reactionGridRef}>
            {(showAllReactions
              ? reactionCapabilitiesData
              : reactionCapabilitiesData.slice(0, cardsToShow)
            ).map((reaction) => (
              <ReactionCapabilityCard key={reaction.id} reaction={reaction} />
            ))}
          </div>
          {/* View More button */}
          {reactionCapabilitiesData.length > cardsToShow && (
            <div className={styles.viewMoreContainer}>
              <button
                className={styles.viewMoreButton}
                onClick={() => setShowAllReactions((prev) => !prev)}
              >
                {showAllReactions ? "View Less" : "View More"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Testing Equipments Section */}
      {selectedTab === "Testing Equipments" && (
        <div className={styles.reactionCapabilities}>
          {/* Heading */}
          <h2 className={styles.heading}>Testing Equipments</h2>
          {/* Grid of Testing Equipment cards */}
          <div className={styles.cardsGrid} ref={equipmentGridRef}>
            {(showAllEquipments
              ? testingEquipmentsData
              : testingEquipmentsData.slice(0, equipmentsToShow)
            ).map((equipment) => (
              <TestingEquipementCard key={equipment.id} equipment={equipment} />
            ))}
          </div>
          {/* View More button */}
          {testingEquipmentsData.length > equipmentsToShow && (
            <div className={styles.viewMoreContainer}>
              <button
                className={styles.viewMoreButton}
                onClick={() => setShowAllEquipments((prev) => !prev)}
              >
                {showAllEquipments ? "View Less" : "View More"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ReactionCapabilitiesTestingEquipments;
